import React, { useState, useEffect, useRef } from 'react';
import { 
  Edit2, 
  Save, 
  UserPlus, 
  UserMinus, 
  LogOut, 
  User, 
  Users, 
  Trash2, 
  Search,
  Settings,
  Shield,
  Bell,
  FileText,
  Check,
  X,
  MoreVertical
} from 'lucide-react';
import { useMessaging } from '../contexts/MessagingContext';
import { collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import { useTheme } from '../contexts/ThemeContext';

const GroupManagementModal = ({ onClose, conversation, currentUser, db }) => {
  const { updateGroup, addParticipant, removeParticipant, leaveGroup } = useMessaging();
  const { theme } = useTheme();

  const [isEditing, setIsEditing] = useState(false);
  const [groupName, setGroupName] = useState(conversation.groupName || '');
  const [groupDescription, setGroupDescription] = useState(conversation.groupDescription || '');
  const [participants, setParticipants] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('participants');
  const addParticipantSectionRef = useRef(null);

  // Récupérer les informations des participants avec les noms des rôles
  useEffect(() => {
    const fetchParticipants = async () => {
      setLoading(true);
      try {
        // Récupérer tous les rôles depuis advanced_roles
        const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
        const rolesMap = new Map();
        rolesSnapshot.forEach(doc => {
          const roleData = doc.data();
          rolesMap.set(doc.id, roleData.name || doc.id);
        });

        const participantPromises = conversation.participants.map(async (participantId) => {
          try {
            const employeeDoc = await getDoc(doc(db, 'employees', participantId));
            if (employeeDoc.exists()) {
              const employeeData = employeeDoc.data();
              const roleId = employeeData.role;
              const roleName = rolesMap.get(roleId) || roleId || 'Collaborateur';
              
              return {
                id: participantId,
                name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() || 'Utilisateur sans nom',
                role: roleName,
                roleId: roleId,
                email: employeeData.email || '',
                isCreator: participantId === conversation.createdBy,
                isActive: employeeData.isActive !== false
              };
            }
          } catch (error) {
            console.error(`Erreur récupération participant ${participantId}:`, error);
          }
          
          return {
            id: participantId,
            name: 'Utilisateur inconnu',
            role: '',
            roleId: '',
            email: '',
            isCreator: false,
            isActive: false
          };
        });

        const participantData = await Promise.all(participantPromises);
        setParticipants(participantData.filter(p => p));

        // Récupérer les employés disponibles pour l'ajout avec les noms des rôles
        const employeesQuery = query(
          collection(db, 'employees'),
          where('isActive', '==', true)
        );
        const employeesSnapshot = await getDocs(employeesQuery);

        const employees = [];
        employeesSnapshot.forEach(doc => {
          const employeeData = doc.data();
          // Exclure les participants déjà dans le groupe
          if (!conversation.participants.includes(doc.id)) {
            const roleId = employeeData.role;
            const roleName = rolesMap.get(roleId) || roleId || 'Collaborateur';
            
            employees.push({
              id: doc.id,
              name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() || 'Utilisateur sans nom',
              role: roleName,
              roleId: roleId,
              email: employeeData.email || ''
            });
          }
        });

        setAvailableEmployees(employees);
      } catch (error) {
        console.error('Erreur récupération participants:', error);
        setError('Impossible de charger les participants');
      } finally {
        setLoading(false);
      }
    };

    fetchParticipants();
  }, [db, conversation]);

  const filteredAvailableEmployees = availableEmployees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSaveGroupInfo = async () => {
    if (!groupName.trim()) {
      setError('Le nom du groupe est obligatoire');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await updateGroup(conversation.id, {
        groupName: groupName.trim(),
        groupDescription: groupDescription.trim()
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur mise à jour groupe:', error);
      setError('Impossible de mettre à jour le groupe');
    } finally {
      setSaving(false);
    }
  };

  const handleAddParticipant = async (employeeId, employeeName) => {
    setSaving(true);
    setError('');
    try {
      await addParticipant(conversation.id, employeeId, employeeName);

      // Mettre à jour localement
      const addedEmployee = availableEmployees.find(e => e.id === employeeId);
      setParticipants(prev => [...prev, {
        id: employeeId,
        name: employeeName,
        role: addedEmployee?.role || 'Collaborateur',
        email: addedEmployee?.email || '',
        isCreator: false,
        isActive: true
      }]);

      setAvailableEmployees(prev => prev.filter(e => e.id !== employeeId));
      setSearchTerm('');

      // Garder la section ouverte et permettre de continuer à ajouter
      setTimeout(() => {
        if (addParticipantSectionRef.current) {
          addParticipantSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    } catch (error) {
      console.error('Erreur ajout participant:', error);
      setError('Impossible d\'ajouter ce participant');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveParticipant = async (participantId, participantName) => {
    if (participants.length <= 2) {
      setError('Un groupe doit avoir au moins 2 participants');
      return;
    }

    if (participantId === conversation.createdBy) {
      setError('Le créateur du groupe ne peut pas être retiré');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir retirer ${participantName} du groupe ?`)) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await removeParticipant(conversation.id, participantId, participantName);

      // Mettre à jour localement
      const removedParticipant = participants.find(p => p.id === participantId);
      setParticipants(prev => prev.filter(p => p.id !== participantId));

      if (removedParticipant) {
        setAvailableEmployees(prev => [...prev, {
          id: participantId,
          name: participantName,
          role: removedParticipant.role,
          email: removedParticipant.email
        }]);
      }
    } catch (error) {
      console.error('Erreur retrait participant:', error);
      setError('Impossible de retirer ce participant');
    } finally {
      setSaving(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir quitter ce groupe ?')) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await leaveGroup(conversation.id);
      onClose();
    } catch (error) {
      console.error('Erreur quitter groupe:', error);
      setError(error.message || 'Impossible de quitter le groupe');
    } finally {
      setSaving(false);
    }
  };

  const isCreator = currentUser?.id === conversation.createdBy;

  return (
    <EnhancedModal
      onClose={onClose}
      size="xl"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            theme === 'dark' ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-100'
          }`}>
            <Users size={24} className="text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Gestion du groupe</h2>
            <p className="text-sm opacity-70">{conversation.groupName}</p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Onglets de navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex space-x-1">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'participants'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
              onClick={() => setActiveTab('participants')}
            >
              Participants ({participants.length})
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'settings'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
              onClick={() => setActiveTab('settings')}
            >
              Paramètres
            </button>
          </div>
        </div>

        {activeTab === 'participants' && (
          <div className="space-y-6">
            {/* En-tête participants */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Membres du groupe</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {participants.length} participant{participants.length > 1 ? 's' : ''}
                </p>
              </div>
              {isCreator && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    theme === 'dark'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                  onClick={() => setShowAddParticipant(!showAddParticipant)}
                  disabled={availableEmployees.length === 0}
                >
                  <UserPlus size={16} />
                  Ajouter
                </button>
              )}
            </div>

            {/* Zone d'ajout de participant */}
            {showAddParticipant && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4" ref={addParticipantSectionRef}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Ajouter un participant</h4>
                  <button
                    onClick={() => {
                      setShowAddParticipant(false);
                      setSearchTerm('');
                    }}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Barre de recherche */}
                <div className="relative mb-4">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un participant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                  />
                </div>

                {/* Liste des participants disponibles */}
                {filteredAvailableEmployees.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filteredAvailableEmployees.map(employee => (
                      <div
                        key={employee.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          theme === 'dark'
                            ? 'hover:bg-gray-700'
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => handleAddParticipant(employee.id, employee.name)}
                      >
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm">
                          <User size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{employee.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {employee.role} • {employee.email}
                          </div>
                        </div>
                        <UserPlus size={16} className="text-blue-600" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'Aucun participant trouvé' : 'Aucun participant disponible'}
                  </div>
                )}
              </div>
            )}

            {/* Liste des participants */}
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {participants.map(participant => (
                  <div
                    key={participant.id}
                    className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                      theme === 'dark'
                        ? 'border-gray-700 hover:bg-gray-800'
                        : 'border-gray-200 hover:bg-gray-50'
                    } ${participant.id === currentUser?.id ? 'bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20' : ''}`}
                  >
                    <div className="relative">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-sm ${
                        participant.isCreator 
                          ? 'bg-purple-600' 
                          : participant.id === currentUser?.id 
                            ? 'bg-blue-600' 
                            : 'bg-gray-600'
                      }`}>
                        <User size={20} />
                      </div>
                      {participant.isCreator && (
                        <div className="absolute -top-1 -right-1 bg-purple-600 text-white rounded-full p-1">
                          <Shield size={12} />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{participant.name}</span>
                        {participant.isCreator && (
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">
                            Créateur
                          </span>
                        )}
                        {participant.id === currentUser?.id && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                            Vous
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {participant.role} {participant.email && `• ${participant.email}`}
                      </div>
                    </div>

                    {isCreator && participant.id !== currentUser?.id && !participant.isCreator && (
                      <button
                        onClick={() => handleRemoveParticipant(participant.id, participant.name)}
                        className={`p-2 rounded-lg transition-colors ${
                          theme === 'dark'
                            ? 'text-red-400 hover:bg-red-900 hover:bg-opacity-20'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                        title="Retirer du groupe"
                      >
                        <UserMinus size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

            {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Informations du groupe */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Informations du groupe</h3>
                {isCreator && !isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm transition-colors ${
                      theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Edit2 size={14} />
                    Modifier
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Nom du groupe
                    </label>
                    <input
                      type="text"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                      }`}
                      placeholder="Nom du groupe"
                      maxLength={50}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Description
                    </label>
                    <textarea
                      value={groupDescription}
                      onChange={(e) => setGroupDescription(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                      }`}
                      placeholder="Description du groupe"
                      rows={3}
                      maxLength={200}
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setGroupName(conversation.groupName || '');
                        setGroupDescription(conversation.groupDescription || '');
                      }}
                      className={`px-4 py-2 border rounded-lg transition-colors ${
                        theme === 'dark'
                          ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleSaveGroupInfo}
                      disabled={saving || !groupName.trim()}
                      className={`flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors ${
                        saving || !groupName.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                      }`}
                    >
                      <Save size={16} />
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Nom :</span>
                    <div className="font-medium">{conversation.groupName}</div>
                  </div>
                  {conversation.groupDescription && (
                    <div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">Description :</span>
                      <div className="font-medium">{conversation.groupDescription}</div>
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Créé le :</span>
                    <div className="font-medium">
                      {conversation.createdAt?.toDate?.()?.toLocaleDateString('fr-FR') || 'Date inconnue'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions du groupe */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h3 className="font-semibold mb-4">Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={handleLeaveGroup}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'text-red-400 hover:bg-red-900 hover:bg-opacity-20'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  <LogOut size={16} />
                  <div className="text-left">
                    <div className="font-medium">Quitter le groupe</div>
                    <div className="text-sm opacity-70">Vous ne recevrez plus les messages de ce groupe</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Message d'erreur */}
        {error && (
          <div className={`p-4 rounded-lg ${
            theme === 'dark'
              ? 'bg-red-900 bg-opacity-20 border border-red-800 text-red-400'
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}>
            {error}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <ModalButton
          variant="secondary"
          onClick={onClose}
        >
          Fermer
        </ModalButton>
      </ModalFooter>
    </EnhancedModal>
  );
};

export default GroupManagementModal;
